
import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';

const PrivacyPolicy = () => {
  return (
    <>
      <SEOHead
        title="Privacy Policy | Milky Way Planner"
        description="Privacy policy for Milky Way Planner. How we handle account data, payments, cookies, and the information you give us when you use the site."
        keywords="privacy policy, milky way planner privacy, data protection"
        canonicalUrl="/privacy"
      />
      <div className="min-h-screen bg-background">
        <section className="bg-slate-950 py-16 sm:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <span className="eyebrow text-gold-light mb-4 block">Legal</span>
            <h1 className="text-3xl md:text-4xl text-white tracking-[-0.03em] mb-4">
              Privacy Policy
            </h1>
            <p className="text-sm text-white/50">Last updated: June 8, 2025</p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <div className="max-w-3xl space-y-8 leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">1. Information We Collect</h2>
              <p className="text-muted-foreground mb-3">
                We collect information you provide directly to us, such as when you create an account, subscribe to our service,
                or contact us for support. This may include:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Email address and password for account creation</li>
                <li>Payment information for subscription processing</li>
                <li>Location data you input for Milky Way calculations</li>
                <li>Usage data and preferences to improve our service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">2. How We Use Your Information</h2>
              <p className="text-muted-foreground mb-3">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Provide and maintain our astrophotography planning service</li>
                <li>Process subscription payments and manage your account</li>
                <li>Calculate astronomical data for your specified locations</li>
                <li>Send you important updates about our service</li>
                <li>Improve our service based on usage patterns</li>
                <li>Respond to your support requests and communications</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">3. Information Sharing</h2>
              <p className="text-muted-foreground">
                We do not sell, trade, or otherwise transfer your personal information to third parties, except as described in this policy.
                We may share your information with trusted service providers who assist us in operating our service, such as payment processors
                and hosting providers, provided they agree to keep this information confidential.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">4. Data Security</h2>
              <p className="text-muted-foreground">
                We implement appropriate security measures to protect your personal information against unauthorized access, alteration,
                disclosure, or destruction. This includes encryption of sensitive data, secure server configurations, and regular security updates.
                However, no method of transmission over the internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">5. Cookies and Tracking</h2>
              <p className="text-muted-foreground">
                We use cookies and similar tracking technologies to enhance your experience on our service. This includes remembering your
                preferences, maintaining your session, and analyzing how you use our service. You can control cookie settings through your browser,
                though disabling cookies may affect some functionality.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">6. Your Rights</h2>
              <p className="text-muted-foreground mb-3">
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Access and update your personal information</li>
                <li>Request deletion of your account and associated data</li>
                <li>Export your data in a portable format</li>
                <li>Opt out of marketing communications</li>
                <li>Request information about how your data is processed</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">7. Data Retention</h2>
              <p className="text-muted-foreground">
                We retain your personal information for as long as necessary to provide our service and fulfill the purposes outlined in this policy.
                When you delete your account, we will delete your personal information within 30 days, except where we are required to retain
                certain information for legal or regulatory purposes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">8. Changes to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page
                and updating the "Last updated" date. Your continued use of our service after such changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">9. Contact Us</h2>
              <p className="text-muted-foreground">
                If you have any questions about this Privacy Policy or how we handle your personal information, please contact us through our
                <Link to="/contact" className="text-primary hover:text-primary/80 ml-1">contact page</Link>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
